import { useHandler } from "@redotech/react-util/hook";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { FlowType } from "@redotech/redo-model/return-flow";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { ProductTagState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/product-tag";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { UploadCsvModal } from "./upload-csv-modal";

export const ProductTagDetails = memo(function ProductTagDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<ProductTagState>) {
  const [inputValue, setInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const stateRef = useRef(state);
  const setStateRef = useRef(setState);

  // Keep refs updated
  useEffect(() => {
    stateRef.current = state;
    setStateRef.current = setState;
  });

  const handleOpenUploadModal = useHandler(() => {
    setUploadModalOpen(true);
  });

  const handleCloseUploadModal = useHandler(() => {
    setUploadModalOpen(false);
  });

  const handleValuesUploaded = useHandler((newTags: string[]) => {
    const currentState = stateRef.current;
    const existingTags = currentState || [];
    const uniqueTags = [...new Set([...existingTags, ...newTags])];
    setStateRef.current(uniqueTags);
  });

  const tags = useMemo(
    () => state.map((tag) => ({ text: tag, color: "gray" as const })),
    [state],
  );

  const description =
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM
      ? "Tags to search for in all selected items"
      : undefined;

  return (
    <>
      <RedoTagInputField
        delimiters={["newline"]}
        description={description}
        inputValue={inputValue}
        label="Product tags"
        placeholder="Type and press Enter to add"
        setInputValue={setInputValue}
        setTags={setState}
        size="sm"
        state={disabled ? "disabled" : "default"}
        tags={tags}
      />
      <Flex dir="column" gap="sm">
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={UploadIcon}
          onClick={handleOpenUploadModal}
          size="sm"
          type="button"
        >
          Upload CSV
        </RedoButton>
      </Flex>
      {uploadModalOpen && (
        <UploadCsvModal
          onClose={handleCloseUploadModal}
          onValuesUploaded={handleValuesUploaded}
          open={uploadModalOpen}
          possibleColumnNames={["product_tag", "tag", "product_tags", "tags"]}
          valueLabel="product tags"
        />
      )}
    </>
  );
});
